import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Flex, Form, Input, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { DEFAULT_LOCALE_CODE, DEFAULT_LOCALE_NAME } from '../../../const';
import { LanguageSelectorCMX } from '../../../components/formItems';
import './styles.less';
import GenericTab from './GenericTab';

/**
 * content for basics tab of just localizations of name and description
 * validation:
 *  - at least one en-US translation
 *  - name, locale, and (optionally) description are required for each item
 * expects form to have following structure:
 * {
 *  translations: [{
 *      locale
 *      name
 *      description
 *  }]
 * }
 * @param {Form} form form object
 * @param {bool!} requireDescription if description is required in object. defaults to false
 */
const BasicsTab = ( { ref, form, item, statuses, parentError, requireDescription = false, onValidateCb, loading, disabled } ) => {

    const [ excludedLocales, setExcludedLocales ] = useState( [] );
    const formNames = [ 'translations' ];

    const minimumLocalesCount = useMemo( () => {
        return item?.locales.length ?? 1;
    }, [ item ] );

    function onLocaleChange() {
        const translations = form?.getFieldValue( 'translations' );
        if ( !translations ) {
            return;
        }
        setExcludedLocales( translations.map( t => t?.locale ) );
    }

    useEffect( () => {
        onLocaleChange();
    }, [ form, loading ] );

    return <GenericTab ref={ ref } loading={ loading } parentError={ parentError } statuses={ statuses }  title='Localizations' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { validateTab, makeDirty } ) => ( <>
            <Flex dir='horizontal' wrap className='translations-list' >
                <Form.List name='translations'
                    rules={ [
                        {
                            validator( _, value ) {
                                if ( value?.length > 0 && value.some( v => v?.locale == DEFAULT_LOCALE_CODE ) ) {
                                    return Promise.resolve();
                                }
                                return Promise.reject( new Error( `${ DEFAULT_LOCALE_NAME } localization is required` ) );
                            },
                        },
                    ] }>
                    { ( fields, { add, remove }, { errors } ) => (
                        <>
                            { /* name is the index number the item is in the form value. Key is the index number it was born with. index is just the index of the list right here */ }
                            { fields.map( ( field ) => {
                                const value = form.getFieldValue( [ 'translations', field.name, 'locale' ] );
                                return (
                                    <Card
                                        key={ field.key }
                                        size="small"
                                        style={ { marginBottom: 16 } }
                                        title={ <>
                                            { value == DEFAULT_LOCALE_CODE ? <Tag color='green'>Default Localization</Tag> : <></> }
                                        </> }
                                        extra={ <>
                                            { field.name >= minimumLocalesCount && <DeleteOutlined style={ { color: 'red' } } onClick={ () => {remove( field.name ); onLocaleChange(); validateTab();} } /> }
                                        </> }
                                    >
                                        <Form.Item required label="Locale" name={ [ field.name, 'locale' ] }
                                            rules={ [
                                                {
                                                    required: true,
                                                    message: 'Localization must have locale'
                                                }
                                            ] }>
                                            <LanguageSelectorCMX
                                                mode="select-single" 
                                                disabled={ value == DEFAULT_LOCALE_CODE || disabled }
                                                excludelocales= { excludedLocales.filter( l => l !== value ) }
                                                hideExcludedLocales={ false }
                                                onChange={ () => {onLocaleChange(); makeDirty();} }
                                            />
                                        </Form.Item>

                                        <Form.Item required label="Name" name={ [ field.name, 'name' ] }
                                            rules={ [
                                                {
                                                    required: true,
                                                    message: 'Localization must have name'
                                                }
                                            ] }>
                                            <Input
                                                disabled={ disabled }
                                                autoComplete='off'
                                                onChange={ () => validateTab() }
                                                placeholder="Name" />
                                        </Form.Item>

                                        <Form.Item required={ requireDescription } label="Description" name={ [ field.name, 'description' ] }
                                            rules={ [
                                                requireDescription ? {
                                                    required: true,
                                                    message: 'Localization must have description'
                                                } : null
                                            ] }>
                                            <Input.TextArea
                                                disabled={ disabled }
                                                onChange={ () => requireDescription ? validateTab() : makeDirty() }
                                                rows={ 3 }
                                                placeholder="Description" />
                                        </Form.Item>
                                    </Card>
                                );
                            } ) }
                            <Button
                                type="dashed"
                                disabled={ disabled }
                                className='add-item-button'
                                icon={ <PlusOutlined /> }
                                onClick={ () => {add(); makeDirty(); } }
                            >
                                Add Localization
                            </Button>
                        </>

                    ) }
                </Form.List>
            </Flex>
        </> ) }
    </GenericTab>;

};

export default BasicsTab;